import { useState } from 'react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { tokens } from '@hotjar/hotsauce';
import type { TopPage } from '@hotjar/heatmaps';
import { generateId } from '@hotjar/webapp/lib/componentId';

const Wrapper = styled.div`
  position: relative;
`;

const Suggestions = styled.div`
  position: absolute;
  top: calc(100% + ${tokens.spacer.x1});
  right: 0;
  left: 0;
  z-index: 9999;
  background-color: ${tokens.color.ui_background};
  border-radius: ${tokens.radius.m};
  padding: ${tokens.spacer.x2} 0;
  ${tokens.elevation(2)};
`;

const SuggestionsHeader = styled.h5`
  ${tokens.text('caption')};
  padding: 0 ${tokens.spacer.x2};
  color: ${tokens.color.text_light};
  font-weight: 400;
`;

const focusedStyle = `
  background-color: ${tokens.color.neutral_100};
    text-decoration: none;
`;

const StyledButton = styled.button`
  color: ${tokens.color.text_default};
  background: none;
  text-transform: none;
  text-shadow: none !important;
  box-shadow: none !important;
  letter-spacing: 0.2px;
  white-space: nowrap;
  border: 1px solid transparent;
  outline: none;
  cursor: pointer;

  &:active,
  &.active {
    ${tokens.elevation(-1)};
  }

  ${tokens.text('caption')};

  padding: ${tokens.spacer.x0pt5} ${tokens.spacer.x2};
  min-height: 32px;
  clear: both;
  width: 100%;

  border-radius: 0;
  text-align: initial;

  &:hover,
  &:active,
  &:focus {
    box-shadow: 0 2px 3px 0 rgba(0, 0, 0, 0.22);
    ${focusedStyle}
  }
`;

const SuggestionText = styled.span`
  display: inline-flex;
  width: 100%;
  flex-wrap: wrap;
`;

const Suggestion = styled.div<{ isSelected?: boolean }>`
  ${(props) =>
    props.isSelected &&
    `
    button {
      ${focusedStyle};
    }
  `}
`;

interface UrlSuggestionsProps {
  children?: React.ReactNode;
  url: string;
  suggestions: TopPage[];
  className?: string;
  onSelect: (url: string, index: number) => void;
  hide: boolean;
  showSuggestions: boolean;
}

export const UrlSuggestions = ({
  children,
  url,
  suggestions,
  className,
  onSelect,
  hide: forceHide,
  showSuggestions,
}: UrlSuggestionsProps): JSX.Element => {
  const { t } = useTranslation();
  const [focusedIndex, setFocusedIndex] = useState(-1);

  const handleSuggestionSelect = (selectedUrl: string, index: number) => {
    onSelect(selectedUrl, index);
  };

  const handleArrowUpEvent = (event: React.KeyboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    const isOnTop = focusedIndex === 0;
    setFocusedIndex(isOnTop ? suggestions.length - 1 : focusedIndex - 1);
  };

  const handleArrowDownEvent = (event: React.KeyboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    const isAtBottom = focusedIndex >= suggestions.length - 1;
    setFocusedIndex(isAtBottom ? 0 : focusedIndex + 1);
  };

  const handleEnter = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (focusedIndex < 0 || focusedIndex >= suggestions.length) {
      return;
    }

    event.preventDefault();
    handleSuggestionSelect(suggestions[focusedIndex].pageUrl, focusedIndex);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    switch (event.key) {
      case 'ArrowUp':
        handleArrowUpEvent(event);
        break;
      case 'ArrowDown':
        handleArrowDownEvent(event);
        break;
      case 'Enter':
        handleEnter(event);
        break;
      default:
        break;
    }
  };

  const renderSuggestions = () => {
    return (
      <Suggestions>
        <SuggestionsHeader>{t('heatmaps:searchbar.suggested-pages-header')}</SuggestionsHeader>
        {suggestions.map((suggestion, index) => (
          <Suggestion key={suggestion.pageUrl} isSelected={focusedIndex === index}>
            <StyledButton
              onPointerDown={(event: React.PointerEvent) => event.preventDefault()}
              onClick={() => {
                handleSuggestionSelect(suggestion.pageUrl, index);
              }}
            >
              <SuggestionText>
                {suggestion.pageUrl
                  .split(new RegExp(`(${url})`, 'g'))
                  .map((part) =>
                    part === url ? (
                      <b key={generateId(`${suggestion.pageUrl}-${part}-b`)}>{part}</b>
                    ) : (
                      <span key={generateId(`${suggestion.pageUrl}-${part}-span`)}>{part}</span>
                    ),
                  )}
              </SuggestionText>
            </StyledButton>
          </Suggestion>
        ))}
      </Suggestions>
    );
  };

  return forceHide ? (
    <>{children}</>
  ) : (
    <Wrapper className={className} onKeyDown={handleKeyDown}>
      {children}
      {showSuggestions && renderSuggestions()}
    </Wrapper>
  );
};
